import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Check, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { useDealTasks } from '@/features/activity/useActivities'
import { useCompleteTask, useCreateTask, useDeleteTask } from '@/features/tasks/useTasks'
import type { Task } from '@/api/tasks'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import ConfirmButton from '@/components/ui/ConfirmButton'
import Card from '@/components/layout/Card'
import { byDueDate, formatShortDate, formatTaskDue, isTaskOverdue } from '@/lib/dealFormat'
import TimeSelect from '@/components/ui/TimeSelect'


/** «+ Add task» form for the Open tasks list (title + optional due date). */
function AddTaskForm({ dealId, onDone }: { dealId: string; onDone: () => void }) {
  const createTask = useCreateTask(dealId)
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [dueTime, setDueTime] = useState('')
  const [error, setError] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    setError(null)
    // Backend expects an ISO-8601 instant. A given time is interpreted in the user's local zone; a
    // date with no time defaults to end-of-day (23:59:59) local, so a date-only task isn't overdue
    // until the day is actually over (isTaskOverdue is time-aware).
    let due: string | null = null
    if (dueDate) {
      due = new Date(`${dueDate}T${dueTime || '23:59:59'}`).toISOString()
    }
    createTask.mutate(
      { title: title.trim(), dueDate: due, dueDateLocal: dueDate || null },
      { onSuccess: onDone, onError: (err) => setError(apiErrorMessage(err)) },
    )
  }

  return (
    <form onSubmit={submit} className="mt-2 space-y-2">
      <input
        autoFocus
        placeholder="Task title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
      />
      {/* Date and time on their own row: this card sits in the narrow right column. */}
      <div className="flex items-center gap-2">
        <input
          type="date"
          title="Due date (optional)"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
        />
        <TimeSelect
          aria-label="Due time (optional)"
          value={dueTime}
          onChange={setDueTime}
          emptyLabel="Any time"
          disabled={!dueDate}
          className="w-28 shrink-0"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={createTask.isPending || !title.trim()}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
        >
          {createTask.isPending ? 'Adding…' : 'Add task'}
        </button>
        <button type="button" onClick={onDone} className="px-2 py-1.5 text-xs text-gray-500 hover:text-brand-dark">
          Cancel
        </button>
      </div>
      {error && <p className="text-danger text-xs">{error}</p>}
    </form>
  )
}

/** One task line — shared by the Open and Completed lists (open rows also get a ✓ complete button). */
function TaskRow({
  task,
  onComplete,
  completing,
  onDelete,
  deleting,
  highlight = false,
}: {
  task: Task
  onComplete?: () => void
  completing?: boolean
  onDelete: () => void
  deleting: boolean
  /** Arrived here from its timeline entry — ring it and bring it into view. */
  highlight?: boolean
}) {
  const done = task.status === 'done'
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (highlight) {
      ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [highlight])
  return (
    <div
      ref={ref}
      className={`flex items-center gap-2 text-xs rounded px-2 py-1.5 transition-shadow ${
        done ? 'text-gray-500 bg-gray-50' : 'text-brand-dark bg-warn/10'
      } ${highlight ? 'ring-2 ring-brand-primary' : ''}`}
    >
      <span className="flex-1 min-w-0">
        {done ? '✓' : '⏰'} {task.title}
        {task.description ? <span className="text-gray-500"> — {task.description}</span> : null}
        {done
          ? task.completedAt && (
              // Both dates, so "done 28 Oct" isn't mistaken for a changed due date — and lateness shows.
              <span className="text-gray-400">
                {task.dueAt && <> · due {formatTaskDue(task.dueAt)}</>} · done {formatShortDate(task.completedAt)}
              </span>
            )
          : task.dueAt && (
              <span className={isTaskOverdue(task.dueAt) ? 'text-danger font-medium' : 'text-gray-400'}>
                {' '}· due {formatTaskDue(task.dueAt)}
              </span>
            )}
      </span>
      {onComplete && (
        <button
          type="button"
          title="Mark complete"
          onClick={onComplete}
          disabled={completing}
          className="shrink-0 text-gray-400 hover:text-success disabled:opacity-50"
        >
          <Check size={14} />
        </button>
      )}
      <ConfirmButton title="Delete task" onConfirm={onDelete} disabled={deleting} className="shrink-0">
        <Trash2 size={14} />
      </ConfirmButton>
    </div>
  )
}

/**
 * The "Tasks" card: the deal's next contact date (part of the deal form the parent saves) plus its
 * open/completed tasks. Tasks are their own mutations and save immediately. The former free-text
 * "Next action" field was removed — it duplicated a task, and one vocabulary (notes + tasks) is the
 * explicit product direction; a task's due date drives the next contact date.
 */
export default function DealNextSteps({
  dealId,
  addingTask: controlledAdding,
  onAddingTaskChange,
  highlightTaskId,
}: {
  dealId: string
  /** Controlled "add task" open state (lets the workspace header's Add task open the form). */
  addingTask?: boolean
  onAddingTaskChange?: (open: boolean) => void
  /** Task to ring and scroll to — set when the user opens a task from its timeline entry. */
  highlightTaskId?: string | null
}) {
  const { data: tasks } = useDealTasks(dealId)
  const completeTask = useCompleteTask()
  const removeTask = useDeleteTask()
  const [internalAdding, setInternalAdding] = useState(false)
  const addingTask = controlledAdding ?? internalAdding
  const setAddingTask = onAddingTaskChange ?? setInternalAdding
  const [showCompleted, setShowCompleted] = useState(false)

  const openTasks = (tasks ?? []).filter((t) => t.status === 'open').sort(byDueDate)
  const completedTasks = (tasks ?? [])
    .filter((t) => t.status === 'done')
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))

  // A completed task lives behind a collapsed section, so opening one from the timeline has to open
  // that section too — otherwise the click looks like it did nothing.
  const highlightIsCompleted = !!highlightTaskId && completedTasks.some((t) => t.id === highlightTaskId)
  const completedOpen = showCompleted || highlightIsCompleted

  return (
    <Card
      title="Tasks"
      action={
        <button
          type="button"
          onClick={() => setAddingTask(!addingTask)}
          className="text-xs text-brand-primary flex items-center gap-1 hover:text-brand-dark"
        >
          <Plus size={13} /> Add task
        </button>
      }
    >
      {/* No separate "next contact date": it is the earliest open task's date, shown in the Snapshot. */}
      {openTasks.length === 0 && (
        <p className="mb-3 text-xs text-gray-500">
          No next step planned. Add a task — its date becomes the deal’s next contact.
        </p>
      )}

      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] uppercase tracking-wide text-gray-400">
          Open tasks{openTasks.length > 0 ? ` (${openTasks.length})` : ''}
        </p>
      </div>
      {openTasks.length === 0 && !addingTask && <p className="text-xs text-gray-400">No open tasks.</p>}
      <div className="space-y-1">
        {openTasks.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            onComplete={() => completeTask.mutate(t.id)}
            completing={completeTask.isPending}
            onDelete={() => removeTask.mutate(t.id)}
            deleting={removeTask.isPending}
            highlight={t.id === highlightTaskId}
          />
        ))}
      </div>
      {addingTask && <AddTaskForm dealId={dealId} onDone={() => setAddingTask(false)} />}

      {completedTasks.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-gray-400 hover:text-brand-dark"
          >
            {completedOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Completed ({completedTasks.length})
          </button>
          {completedOpen && (
            <div className="space-y-1 mt-1">
              {completedTasks.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  onDelete={() => removeTask.mutate(t.id)}
                  deleting={removeTask.isPending}
                  highlight={t.id === highlightTaskId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
